import React from "react";
import { Box, Chip, LinearProgress } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import { WarningAmberRoundedIcon } from "styles/StyledMUI";
import { colorTokens, withAlpha } from "styles/globalStyles";
import type { SmsDispatchCounters, SmsDispatchItem } from "../hooks/useExcursionSmsDispatch";
import SmsStatusRow from "./SmsStatusRow";
import { SMS_STATUS_META } from "./smsStatusMeta";

type ExcursionSmsSendProgressProps = {
  items: SmsDispatchItem[];
  running: boolean;
  globalError: string | null;
  counters: SmsDispatchCounters;
};

type ItemGroup = { excursionId: number; label: string; items: SmsDispatchItem[] };

function groupItems(items: SmsDispatchItem[], sortAnomaliesFirst: boolean): ItemGroup[] {
  const groups = new Map<number, ItemGroup>();
  for (const it of items) {
    const group = groups.get(it.excursionId);
    if (group) group.items.push(it);
    else
      groups.set(it.excursionId, {
        excursionId: it.excursionId,
        label: it.groupLabel,
        items: [it],
      });
  }

  const list = [...groups.values()];
  if (!sortAnomaliesFirst) return list;

  return list.map((g) => ({
    ...g,
    items: [...g.items].sort(
      (a, b) => SMS_STATUS_META[a.status].rank - SMS_STATUS_META[b.status].rank,
    ),
  }));
}

function SmsDispatchRow({ item }: { item: SmsDispatchItem }) {
  return (
    <SmsStatusRow
      status={item.status}
      title={item.recipient.label}
      subtitle={item.recipient.phone}
      error={item.error}
    />
  );
}

export default function ExcursionSmsSendProgress(props: ExcursionSmsSendProgressProps) {
  const { items, running, globalError, counters } = props;
  const groups = React.useMemo(() => groupItems(items, !running), [items, running]);
  const progress = counters.total > 0 ? Math.round((counters.done / counters.total) * 100) : 0;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <LinearProgress
          determinate
          value={progress}
          color={counters.failed > 0 ? "danger" : "primary"}
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
          <Chip size="sm" variant="soft" color="primary">
            Transmis · {counters.sent}
          </Chip>
          {counters.refused > 0 ? (
            <Chip size="sm" variant="soft" color="warning">
              Refusés · {counters.refused}
            </Chip>
          ) : null}
          {counters.failed > 0 ? (
            <Chip size="sm" variant="soft" color="danger">
              Échecs · {counters.failed}
            </Chip>
          ) : null}
        </Box>
        {globalError ? (
          <Box
            role="alert"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.25,
              borderRadius: "8px",
              backgroundColor: withAlpha(colorTokens.status.danger, 0.12),
              border: `1px solid ${withAlpha(colorTokens.status.danger, 0.4)}`,
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: colorTokens.status.danger, flexShrink: 0 }} />
            <DarkTypography level="body-sm" colorVariant="primary">
              Envoi interrompu : {globalError}
            </DarkTypography>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          pr: 0.25,
        }}
      >
        {groups.map((group) => (
          <Box
            key={group.excursionId}
            sx={{
              flexShrink: 0,
              borderRadius: "8px",
              border: `1px solid ${colorTokens.border.default}`,
              backgroundColor: colorTokens.bg.surfaceElevated,
              p: { xs: 0.75, sm: 1 },
              minWidth: 0,
            }}
          >
            <DarkTypography level="body-xs" colorVariant="title" sx={{ mb: 0.5, px: 0.5 }}>
              {group.label} · {group.items.length}
            </DarkTypography>
            {group.items.map((item) => (
              <SmsDispatchRow key={item.key} item={item} />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
